

import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import Modal from '../../../components/base/Modal';

interface Zahlung {
  id: string;
  titel: string;
  kontakt: string;
  betrag: number;
  faelligkeitsdatum: string;
  status: 'offen' | 'bezahlt' | 'ueberfaellig';
  notizen?: string;
  datei_url?: string;
  datei_name?: string;
  erstellt_am: string;
  bezahlt_am?: string;
  bezahlt_von?: string;
  aktualisiert_am?: string;
}

interface Rueckzahlung {
  id: string;
  titel: string;
  betrag: number;
  faelligkeitsdatum: string;
  status: 'offen' | 'bezahlt' | 'ueberfaellig';
  kontakt?: string;
  notizen?: string;
  datei_url?: string;
  datei_name?: string;
  erstellt_am: string;
  bezahlt_am?: string;
  bezahlt_von?: string;
  aktualisiert_am?: string;
}

interface PaymentsManagerProps {
  payments: any[];
  refunds: any[];
  onPaymentSaved: () => void;
  onRefundSaved: () => void;
}

const kategorien = [
  'Bürokosten',
  'Marketing',
  'Reisekosten',
  'Wartung',
  'Miete',
  'Versicherung',
  'Software',
  'Hardware',
  'Beratung',
  'Sonstiges'
];

export default function PaymentsManager({ payments, refunds, onPaymentSaved, onRefundSaved }: PaymentsManagerProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'offen' | 'bezahlt' | 'rueckzahlungen'>('offen');
  const [zahlungen, setZahlungen] = useState<Zahlung[]>([]);
  const [rueckzahlungen, setRueckzahlungen] = useState<Rueckzahlung[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRueckzahlungModalOpen, setIsRueckzahlungModalOpen] = useState(false);
  const [selectedZahlung, setSelectedZahlung] = useState<Zahlung | null>(null);
  const [isAddingRueckzahlung, setIsAddingRueckzahlung] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    titel: '',
    kontakt: '',
    betrag: '',
    faelligkeitsdatum: '',
    notizen: '',
    document: null as File | null
  });

  const [refundFormData, setRefundFormData] = useState({
    titel: '',
    betrag: '',
    grund: '',
    iban: '',
    bankname: '',
    kontoinhaber: '',
    bankadresse: '',
    bic: '',
    notizen: '',
    document: null as File | null,
    faelligkeitsdatum: ''
  });

  const [rueckzahlungFormData, setRueckzahlungFormData] = useState({
    titel: '',
    betrag: '',
    faelligkeitsdatum: '',
    kontakt: '',
    notizen: ''
  });

  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadZahlungen(),
        loadRueckzahlungen()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadZahlungen = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('zahlungen')
        .select('*')
        .eq('user_id', user.id)
        .gte('betrag', 0)
        .order('erstellt_am', { ascending: false });

      if (error) throw error;

      const today = new Date().toISOString().split('T')[0];
      const updatedZahlungen = (data || []).map(zahlung => ({
        ...zahlung,
        status: zahlung.status === 'offen' && zahlung.faelligkeitsdatum < today ? 'ueberfaellig' : zahlung.status
      }));

      setZahlungen(updatedZahlungen);
    } catch (error) {
      console.error('Error loading payments:', error);
      setZahlungen([]);
    }
  };

  const loadRueckzahlungen = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('zahlungen')
        .select('*')
        .eq('user_id', user.id)
        .lt('betrag', 0)
        .order('erstellt_am', { ascending: false });

      if (error) throw error;

      const rueckzahlungsData = (data || []).map(item => ({
        id: item.id,
        titel: item.titel.replace('[RÜCKZAHLUNG] ', ''),
        betrag: Math.abs(item.betrag),
        faelligkeitsdatum: item.faelligkeitsdatum,
        status: item.status,
        kontakt: item.kontakt,
        notizen: item.notizen?.replace('RÜCKZAHLUNG: ', '') || '',
        datei_url: item.datei_url,
        datei_name: item.datei_name,
        erstellt_am: item.erstellt_am,
        aktualisiert_am: item.aktualisiert_am,
        bezahlt_am: item.bezahlt_am,
        bezahlt_von: item.bezahlt_von
      }));

      setRueckzahlungen(rueckzahlungsData);
    } catch (error) {
      console.error('Error loading refunds:', error);
      setRueckzahlungen([]);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRueckzahlungInputChange = (field: string, value: string) => {
    setRefundFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      titel: '',
      kontakt: '',
      betrag: '',
      faelligkeitsdatum: '',
      notizen: '',
      document: null
    });
  };

  const resetRefundForm = () => {
    setRefundFormData({
      titel: '',
      betrag: '',
      grund: '',
      iban: '',
      bankname: '',
      kontoinhaber: '',
      bankadresse: '',
      bic: '',
      notizen: '',
      document: null,
      faelligkeitsdatum: ''
    });
    setSelectedZahlung(null);
  };

  // Upload file to Supabase Storage
  const uploadDocument = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/payments/${fileName}`;

      const { data, error } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (error) {
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return { url: publicUrl };
    } catch (error) {
      return { url: '', error };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.titel.trim() || !formData.kontakt.trim() || !formData.betrag || !user) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    try {
      let documentUrl = null;
      let documentName = null;

      // Upload document if provided
      if (formData.document) {
        const { url, error } = await uploadDocument(formData.document);
        if (error) {
          alert('Fehler beim Hochladen des Dokuments: ' + error.message);
          return;
        }
        documentUrl = url;
        documentName = formData.document.name;
      }

      const zahlungData = {
        user_id: user.id,
        titel: formData.titel,
        kontakt: formData.kontakt,
        betrag: parseFloat(formData.betrag),
        faelligkeitsdatum: formData.faelligkeitsdatum,
        status: 'offen',
        notizen: formData.notizen || null,
        datei_url: documentUrl,
        datei_name: documentName,
        erstellt_am: new Date().toISOString(),
        aktualisiert_am: new Date().toISOString()
      };

      const { error } = await supabase
        .from('zahlungen')
        .insert([zahlungData]);

      if (error) throw error;

      await loadAllData();
      onPaymentSaved();
      setIsModalOpen(false);
      resetForm();
      alert('✅ Zahlung wurde erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('❌ Fehler beim Erstellen der Zahlung.');
    }
  };

  const handleAddRueckzahlung = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !refundFormData.titel || !refundFormData.betrag) {
      alert('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setIsAddingRueckzahlung(true);

    try {
      let documentUrl = null;
      let documentName = null;

      if (refundFormData.document) {
        const { url, error } = await uploadDocument(refundFormData.document);
        if (error) {
          alert('Fehler beim Hochladen des Dokuments: ' + error.message);
          return;
        }
        documentUrl = url;
        documentName = refundFormData.document.name;
      }

      // Erstelle detaillierte Notizen mit Bankdaten
      const detailledNotes = `
RÜCKZAHLUNGSGRUND: ${refundFormData.grund}

BANKVERBINDUNG:
Kontoinhaber: ${refundFormData.kontoinhaber}
IBAN: ${refundFormData.iban}
${refundFormData.bic ? `BIC/SWIFT: ${refundFormData.bic}` : ''}
Bank: ${refundFormData.bankname}
Bankadresse: ${refundFormData.bankadresse}

${refundFormData.notizen ? `ZUSÄTZLICHE NOTIZEN:\n${refundFormData.notizen}` : ''}
      `.trim();

      const rueckzahlungData = {
        user_id: user.id,
        titel: `[RÜCKZAHLUNG] ${refundFormData.titel}`,
        betrag: -Math.abs(parseFloat(refundFormData.betrag)),
        faelligkeitsdatum: refundFormData.faelligkeitsdatum,
        status: 'offen',
        kontakt: refundFormData.kontoinhaber || null,
        notizen: detailledNotes,
        datei_url: documentUrl,
        datei_name: documentName,
        erstellt_am: new Date().toISOString(),
        aktualisiert_am: new Date().toISOString()
      };

      const { error } = await supabase
        .from('zahlungen')
        .insert([rueckzahlungData]);

      if (error) {
        console.error('Rückzahlung error:', error);
        throw error;
      }

      await loadAllData();
      onRefundSaved();
      setIsRueckzahlungModalOpen(false);
      resetRefundForm();
      alert('✅ Rückzahlung erfolgreich angefordert!');
    } catch (error: any) {
      console.error('Error adding refund:', error);
      if (error.code === '23514') {
        alert('❌ Datenbank-Konfigurationsfehler. Verwende alternativen Speicherweg...');
      } else if (error.code === '23505') {
        alert('❌ Diese Rückzahlung existiert bereits.');
      } else {
        alert('❌ Fehler beim Anfordern der Rückzahlung: ' + (error.message || 'Unbekannter Fehler'));
      }
    } finally {
      setIsAddingRueckzahlung(false);
    }
  };

  // Download file
  const handleDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const markAsPaid = async (id: string) => {
    try {
      const { error } = await supabase
        .from('zahlungen')
        .update({
          status: 'bezahlt',
          bezahlt_am: new Date().toISOString(),
          aktualisiert_am: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      await loadAllData();
      onPaymentSaved();
      alert('✅ Zahlung wurde als bezahlt markiert!');
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      alert('❌ Fehler beim Markieren der Zahlung.');
    }
  };

  const deleteZahlung = async (id: string) => {
    if (!confirm('Möchten Sie diese Zahlung wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('zahlungen')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadAllData();
      onPaymentSaved();
      alert('✅ Zahlung wurde gelöscht!');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('❌ Fehler beim Löschen der Zahlung.');
    }
  };

  const formatCurrency = (amount: number) => {
    return `CHF ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'offen':
        return 'bg-yellow-100 text-yellow-800';
      case 'bezahlt':
        return 'bg-green-100 text-green-800';
      case 'ueberfaellig':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredZahlungen = zahlungen.filter(zahlung => {
    if (activeTab === 'offen') {
      return zahlung.status === 'offen' || zahlung.status === 'ueberfaellig';
    } else if (activeTab === 'bezahlt') {
      return zahlung.status === 'bezahlt';
    }
    return true;
  });

  const offeneZahlungen = zahlungen.filter(z => z.status === 'offen' || z.status === 'ueberfaellig');
  const bezahlteZahlungen = zahlungen.filter(z => z.status === 'bezahlt');

  const offenerBetrag = offeneZahlungen.reduce((sum, z) => sum + z.betrag, 0);
  const bezahlterBetrag = bezahlteZahlungen.reduce((sum, z) => sum + z.betrag, 0);
  const rueckzahlungsBetrag = rueckzahlungen.reduce((sum, r) => sum + r.betrag, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Zahlungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Offene Rechnungen</p>
              <p className="text-2xl font-bold text-red-700">{formatCurrency(offenerBetrag)}</p>
              <p className="text-sm text-red-600">{offeneZahlungen.length} Rechnungen</p>
            </div>
            <i className="ri-file-list-line text-3xl text-red-600"></i>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Bezahlte Rechnungen</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(bezahlterBetrag)}</p>
              <p className="text-sm text-green-600">{bezahlteZahlungen.length} Rechnungen</p>
            </div>
            <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
          </div>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600">Rückzahlungen</p>
              <p className="text-2xl font-bold text-orange-700">{formatCurrency(rueckzahlungsBetrag)}</p>
              <p className="text-sm text-orange-600">{rueckzahlungen.length} Anfragen</p>
            </div>
            <i className="ri-refund-line text-3xl text-orange-600"></i>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => setIsModalOpen(true)}
          className="flex-1 sm:flex-none"
        >
          <i className="ri-add-line mr-2"></i>
          Neue Zahlung
        </Button>
        
        <Button
          onClick={() => setIsRueckzahlungModalOpen(true)}
          variant="secondary"
          className="flex-1 sm:flex-none"
        >
          <i className="ri-refund-line mr-2"></i>
          Rückzahlung anfordern
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-2 sm:gap-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('offen')}
            className={`py-2 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'offen'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-file-list-line mr-1 sm:mr-2"></i>
            <span className="hidden sm:inline">Offene Zahlungen</span>
            <span className="sm:hidden">Offen</span>
            <span className="ml-1">({offeneZahlungen.length})</span>
          </button>
          
          <button
            onClick={() => setActiveTab('bezahlt')}
            className={`py-2 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'bezahlt'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-checkbox-circle-line mr-1 sm:mr-2"></i>
            <span className="hidden sm:inline">Bezahlte Rechnungen</span>
            <span className="sm:hidden">Bezahlt</span>
            <span className="ml-1">({bezahlteZahlungen.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('rueckzahlungen')}
            className={`py-2 px-3 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer flex-shrink-0 ${
              activeTab === 'rueckzahlungen'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <i className="ri-refund-line mr-1 sm:mr-2"></i>
            <span className="hidden sm:inline">Rückzahlungen</span>
            <span className="sm:hidden">Rückzahl.</span>
            <span className="ml-1">({rueckzahlungen.length})</span>
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'rueckzahlungen' ? (
        // Rückzahlungen Content - Mobile Optimized
        <div className="space-y-3">
          {rueckzahlungen.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <i className="ri-refund-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Rückzahlungen</h3>
              <p className="text-gray-500">Es wurden noch keine Rückzahlungen angefordert.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rueckzahlungen.map(rueckzahlung => (
                <div key={rueckzahlung.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  {/* Mobile Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-3">
                      <h4 className="text-base font-medium text-gray-900 leading-tight mb-1">
                        {rueckzahlung.titel}
                      </h4>
                      {rueckzahlung.kontakt && (
                        <p className="text-sm text-gray-600 mb-1">{rueckzahlung.kontakt}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        Fällig: {formatDate(rueckzahlung.faelligkeitsdatum)}
                      </p>
                    </div>
                    
                    {/* Mobile Actions */}
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <p className="text-lg font-bold text-orange-600">
                        {formatCurrency(rueckzahlung.betrag)}
                      </p>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(rueckzahlung.status)}`}>
                        {rueckzahlung.status}
                      </span>
                    </div>
                  </div>
                  
                  {/* Mobile Action Buttons */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      {rueckzahlung.datei_name && rueckzahlung.datei_url && (
                        <button
                          onClick={() => handleDownload(rueckzahlung.datei_url!, rueckzahlung.datei_name!)}
                          className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md cursor-pointer transition-colors"
                        >
                          <i className="ri-download-line mr-1"></i>
                          Dokument
                        </button>
                      )}
                      {rueckzahlung.status !== 'bezahlt' && (
                        <button
                          onClick={() => markAsPaid(rueckzahlung.id)}
                          className="flex items-center px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-md cursor-pointer transition-colors"
                        >
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          Bezahlt
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={() => deleteZahlung(rueckzahlung.id)}
                      className="flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Löschen
                    </button>
                  </div>
                  
                  {/* Notes */}
                  {rueckzahlung.notizen && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600">
                        <strong>Notizen:</strong> {rueckzahlung.notizen}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Zahlungen Content - Mobile Optimized
        <div className="space-y-3">
          {filteredZahlungen.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <i className="ri-file-list-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Keine {activeTab === 'offen' ? 'offenen' : 'bezahlten'} Zahlungen
              </h3>
              <p className="text-gray-500">
                {activeTab === 'offen' 
                  ? 'Alle Rechnungen sind bezahlt oder es wurden noch keine erstellt.'
                  : 'Es wurden noch keine Zahlungen als bezahlt markiert.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredZahlungen.map(zahlung => (
                <div key={zahlung.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  {/* Mobile Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-3">
                      <h4 className="text-base font-medium text-gray-900 leading-tight mb-1">
                        {zahlung.titel}
                      </h4>
                      <p className="text-sm text-gray-600 mb-1">{zahlung.kontakt}</p>
                      <p className="text-xs text-gray-500">
                        Fällig: {formatDate(zahlung.faelligkeitsdatum)}
                      </p>
                    </div>
                    
                    {/* Mobile Amount & Status */}
                    <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(zahlung.betrag)}
                      </p>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(zahlung.status)}`}>
                        {zahlung.status}
                      </span>
                    </div>
                  </div>
                  
                  {/* Mobile Action Buttons */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      {zahlung.datei_name && zahlung.datei_url && (
                        <button
                          onClick={() => handleDownload(zahlung.datei_url!, zahlung.datei_name!)}
                          className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md cursor-pointer transition-colors"
                        >
                          <i className="ri-download-line mr-1"></i>
                          Dokument
                        </button>
                      )}
                      {zahlung.status !== 'bezahlt' && (
                        <button
                          onClick={() => markAsPaid(zahlung.id)}
                          className="flex items-center px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-md cursor-pointer transition-colors"
                        >
                          <i className="ri-checkbox-circle-line mr-1"></i>
                          Bezahlt
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={() => deleteZahlung(zahlung.id)}
                      className="flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Löschen
                    </button>
                  </div>
                  
                  {/* Notes */}
                  {zahlung.notizen && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-md">
                      <p className="text-xs text-gray-600">
                        <strong>Notizen:</strong> {zahlung.notizen}
                      </p>
                    </div>
                  )}
                  
                  {/* Payment Date */}
                  {zahlung.bezahlt_am && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                        <i className="ri-calendar-check-line mr-1"></i>
                        Bezahlt: {formatDate(zahlung.bezahlt_am)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Payment Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Neue Zahlung erstellen"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Titel *"
            value={formData.titel}
            onChange={e => handleInputChange('titel', e.target.value)}
            placeholder="z.B. Rechnung #12345"
            required
          />
          
          <Input
            label="Kontakt/Empfänger *"
            value={formData.kontakt}
            onChange={e => handleInputChange('kontakt', e.target.value)}
            placeholder="Name oder Firma"
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Betrag (CHF) *"
              type="number"
              step="0.01"
              value={formData.betrag}
              onChange={e => handleInputChange('betrag', e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <Input
            label="Notizen"
            value={formData.notizen}
            onChange={e => handleInputChange('notizen', e.target.value)}
            multiline
            rows={3}
            placeholder="Zusätzliche Informationen..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-attachment-line mr-1"></i> Dokument anhängen (optional)
            </label>
            <input
              type="file"
              onChange={e => setFormData(prev => ({ ...prev, document: e.target.files?.[0] || null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
            />
            <p className="mt-2 text-xs text-gray-500">
              PDF, Word, Excel oder Bild-Dateien (max. 10MB)
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:flex-1"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit" className="w-full sm:flex-1">
              Zahlung erstellen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Rückzahlung Modal */}
      <Modal
        isOpen={isRueckzahlungModalOpen}
        onClose={() => {
          setIsRueckzahlungModalOpen(false);
          resetRefundForm();
        }}
        title="Rückzahlung anfordern"
      >
        <form onSubmit={handleAddRueckzahlung} className="space-y-4">
          <Input
            label="Titel der Rückzahlung *"
            value={refundFormData.titel}
            onChange={e => handleRueckzahlungInputChange('titel', e.target.value)}
            placeholder="z.B. Rückzahlung für Überzahlung"
            required
          />

          <Input
            label="Kontakt/Empfänger"
            value={refundFormData.kontoinhaber}
            onChange={e => handleRueckzahlungInputChange('kontoinhaber', e.target.value)}
            placeholder="Name oder Firma"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Rückzahlungsbetrag (CHF) *"
              type="number"
              step="0.01"
              value={refundFormData.betrag}
              onChange={e => handleRueckzahlungInputChange('betrag', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Fälligkeitsdatum"
              type="date"
              value={refundFormData.faelligkeitsdatum}
              onChange={e => handleRueckzahlungInputChange('faelligkeitsdatum', e.target.value)}
            />
          </div>

          <Input
            label="Grund"
            value={refundFormData.grund}
            onChange={e => handleRueckzahlungInputChange('grund', e.target.value)}
            placeholder="Grund für die Rückzahlung"
          />

          <Input
            label="IBAN"
            value={refundFormData.iban}
            onChange={e => handleRueckzahlungInputChange('iban', e.target.value)}
            placeholder="IBAN"
          />

          <Input
            label="BIC / SWIFT"
            value={refundFormData.bic}
            onChange={e => handleRueckzahlungInputChange('bic', e.target.value)}
            placeholder="BIC / SWIFT (optional)"
          />

          <Input
            label="Bankname"
            value={refundFormData.bankname}
            onChange={e => handleRueckzahlungInputChange('bankname', e.target.value)}
            placeholder="Bankname"
          />

          <Input
            label="Bankadresse"
            value={refundFormData.bankadresse}
            onChange={e => handleRueckzahlungInputChange('bankadresse', e.target.value)}
            placeholder="Bankadresse"
          />

          <Input
            label="Notizen"
            value={refundFormData.notizen}
            onChange={e => handleRueckzahlungInputChange('notizen', e.target.value)}
            multiline
            rows={3}
            placeholder="Zusätzliche Informationen..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-attachment-line mr-1"></i> Beleg/Dokument anhängen (optional)
            </label>
            <input
              type="file"
              onChange={e => setRefundFormData(prev => ({ ...prev, document: e.target.files?.[0] || null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
            />
            <p className="mt-2 text-xs text-gray-500">
              PDF, Word, Excel oder Bild-Dateien (max. 10MB)
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:flex-1"
              onClick={() => {
                setIsRueckzahlungModalOpen(false);
                resetRefundForm();
              }}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              className="w-full sm:flex-1"
              disabled={isAddingRueckzahlung}
            >
              {isAddingRueckzahlung ? (
                <>
                  <i className="ri-loader-line mr-2 animate-spin"></i>
                  Speichere...
                </>
              ) : (
                <>
                  <i className="ri-refund-line mr-2"></i>
                  Rückzahlung anfordern
                </>
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

